import { PhoneNumberPurchase, SmsEnablementStatus } from '@/apis/business/setting/phoneNumbers/types';
import { titleCase } from 'utils/helperFunctions';

/** Shared display shape for legacy purchases and telephony_endpoints. */
export type PhoneNumberDisplayRow = {
  phone_number?: string | null;
  number_type?: string | null;
  extension?: string | null;
  kind?: string | null;
  sip_config?: Record<string, unknown> | null;
  connection_config?: Record<string, unknown> | null;
  country_code?: string | null;
  provider?: string | null;
  provider_name?: string | null;
};

const CAPABILITY_ORDER = ['voice', 'sip', 'sms', 'hosted_sms', 'a2p'] as const;

const normalizeCountryCode = (countryCode?: string | null): 'US' | 'CA' | null => {
  const code = countryCode?.toUpperCase().replace(/[^A-Z]/g, '') ?? '';
  if (code === 'CA' || code === 'CAN' || code === 'CANADA') return 'CA';
  if (code === 'US' || code === 'USA') return 'US';
  return null;
};

export const capabilityColor = (capability?: string | null) => {
  switch ((capability || '').toLowerCase()) {
    case 'voice':
      return { bg: '#F3E8FF', color: '#7C3AED' };
    case 'sip':
      return { bg: '#DBEAFE', color: '#2563EB' };
    case 'sms':
      return { bg: '#D1FAE5', color: '#047857' };
    case 'hosted_sms':
      return { bg: '#FFEDD5', color: '#C2410C' };
    case 'a2p':
      return { bg: '#FFEDD5', color: '#C2410C' };
    default:
      return { bg: '#F3F4F6', color: '#6B7280' };
  }
};

export const capabilityLabel = (capability: string) => {
  switch ((capability || '').toLowerCase()) {
    case 'voice':
      return 'VOICE';
    case 'sip':
      return 'SIP';
    case 'sms':
      return 'SMS';
    case 'hosted_sms':
      return 'HOSTED SMS';
    case 'a2p':
      return 'A2P';
    default:
      return (capability || '').replace(/_/g, ' ').toUpperCase();
  }
};

/** Labels shown on the Connect SMS screen (matches design). */
export const connectSmsCapabilityLabel = (capability: string) => capabilityLabel(capability);

export const displayCapabilities = (capabilities?: string[] | null) => {
  const set = new Set((capabilities ?? []).map((cap) => cap.toLowerCase()));
  return CAPABILITY_ORDER.filter((cap) => set.has(cap));
};

export const countryLabel = (countryCode?: string | null) => {
  if (!countryCode) return '';
  const normalized = countryCode.toUpperCase();
  if (normalized === 'US' || normalized === 'USA') return 'USA';
  if (normalized === 'CA' || normalized === 'CAN') return 'Canada';
  return normalized;
};

export const isUsNumber = (row: PhoneNumberDisplayRow) => normalizeCountryCode(row.country_code) === 'US';

export const isCanadaNumber = (row: PhoneNumberDisplayRow) => normalizeCountryCode(row.country_code) === 'CA';

export const isExtensionNumber = (row: PhoneNumberDisplayRow) => {
  if (row.kind === 'extension') return true;
  const fromAttr = row.number_type?.toString().toLowerCase();
  const fromSip = (row.sip_config as { number_type?: string } | null | undefined)?.number_type
    ?.toString()
    .toLowerCase();
  const fromConnection = (row.connection_config as { number_type?: string } | null | undefined)?.number_type
    ?.toString()
    .toLowerCase();
  return fromAttr === 'extension' || fromSip === 'extension' || fromConnection === 'extension';
};

/** Connect SMS: real phone numbers only (no extensions / SIP user ids). */
export const isConnectSmsPhoneNumber = (row: PhoneNumberDisplayRow) => {
  if (isExtensionNumber(row)) return false;

  const value = (row.phone_number || '').trim();
  // E.164-style DID, e.g. +14378264380 — excludes "1101", "user_…"
  return /^\+[1-9]\d{6,14}$/.test(value);
};

/** Matches backend Business::PhoneNumberPurchaseSerializer#sms_connectable */
export const isSmsAssignable = (row: PhoneNumberPurchase | null | undefined) => row?.sms_connectable === true;

export type ConnectSmsPanelType = 'ready' | 'us_compliance' | 'host_sms_required' | 'not_sms_capable' | 'none';

/** Matches backend Business::PhoneNumberPurchaseSerializer#sms_enablement_status */
export const connectSmsPanelType = (row: PhoneNumberPurchase | null): ConnectSmsPanelType => {
  if (!row) return 'none';
  if (isExtensionNumber(row)) return 'not_sms_capable';

  const status = row.sms_enablement_status as SmsEnablementStatus | undefined;

  switch (status) {
    case 'ready':
      return 'ready';
    case 'not_sms_capable':
      return 'not_sms_capable';
    case 'needs_a2p':
      return 'us_compliance';
    case 'needs_host_sms':
      return isUsNumber(row) ? 'us_compliance' : 'host_sms_required';
    default:
      return 'none';
  }
};

export const formatNumberOrExtension = (row: PhoneNumberDisplayRow): string => {
  if (isExtensionNumber(row)) {
    return row.extension || row.phone_number || '';
  }

  return row.phone_number || '';
};

export const providerLabel = (row: PhoneNumberDisplayRow) => {
  const provider = row.provider_name || (row.provider ? titleCase(row.provider.replace(/_/g, ' ')) : '');
  const country = countryLabel(row.country_code);
  if (!provider) return country;
  if (!country) return provider;
  return `${provider} - ${country}`;
};
